<template>
  <div class="row flex-center min-vh-100 py-6">
    <div class="col-sm-10 col-md-8 col-lg-6 col-xl-5 col-xxl-4">
      <div class="text-center mb-3">
        <img src="logo_horizontal.png" alt="" height="50">
      </div>
      <div class="card">
        <div class="card-body p-4 p-sm-5">

          <!-- Loading spinner while we resolve the session against the backend. -->
          <div v-if="state === 'loading'" class="text-center py-4">
            <div class="spinner-border" role="status"><span class="visually-hidden">Loading…</span></div>
          </div>

          <!-- Terminal error states (no session id in URL, session expired/unknown, no such client). -->
          <div v-else-if="state === 'error'" class="text-center">
            <div class="bg-warning me-2 icon-item d-inline-block mb-3">
              <span class="fas fa-exclamation-triangle text-white fs-7"></span>
            </div>
            <h5>Authorization request can't be completed</h5>
            <p class="text-muted fs-10 mb-4">{{ errorMessage }}</p>
            <router-link :to="{ name: 'MyBots' }" class="btn btn-outline-primary">Back to Signum</router-link>
          </div>

          <!-- Consent prompt. -->
          <div v-else-if="state === 'ready'">
            <h5 class="text-center mb-2">Authorize {{ consent.client_name }}</h5>
            <p class="text-muted fs-10 text-center mb-4">
              {{ consent.client_name }} is asking to access your Signum account.
              Pick which permissions to grant. You can change or revoke this anytime.
            </p>

            <div class="mb-4">
              <div v-for="scope in consent.scopes" :key="scope" class="form-check mb-2 text-start">
                <input
                  :id="`scope-${scope}`"
                  v-model="selected[scope]"
                  :disabled="scope === 'read'"
                  class="form-check-input"
                  type="checkbox">
                <label :for="`scope-${scope}`" class="form-check-label">
                  <strong>{{ scopeLabel(scope) }}</strong>
                  <span v-if="scope === 'read'" class="badge bg-secondary ms-2 fs-11">always granted</span>
                  <div class="text-muted fs-10">{{ scopeDescription(scope) }}</div>
                </label>
              </div>
            </div>

            <div v-if="submitError"
                 class="alert alert-danger border-0 d-flex align-items-center text-start mb-3"
                 role="alert">
              <p class="mb-0 flex-1 fs-10">{{ submitError }}</p>
            </div>

            <div class="d-grid gap-2">
              <button class="btn btn-primary" :disabled="submitting" @click="submit('allow')">
                <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
                Allow
              </button>
              <button class="btn btn-outline-secondary" :disabled="submitting" @click="submit('deny')">
                Deny
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiFetch } from '@/api/apiFetch'

// User-facing descriptions for each known scope. Unknown scopes fall back to the
// raw identifier (forward-compatible if the backend ever advertises a new scope).
const SCOPE_LABELS = {
  read: 'View account data',
  write: 'Modify bots and settings',
  trading: 'Execute trades on your behalf',
  offline_access: 'Stay signed in',
}
const SCOPE_DESCRIPTIONS = {
  read: 'Read your bots, trades, and Trend Radar data.',
  write: 'Create or change bots and account settings.',
  trading: 'Place orders on the exchanges your bots are connected to. High impact — only enable for clients you trust.',
  offline_access: 'Re-issue access tokens in the background so you don\'t have to re-authorize every hour.',
}

export default {
  data () {
    return {
      state: 'loading', // 'loading' | 'error' | 'ready'
      errorMessage: '',
      consent: null,    // { client_name, scopes, redirect_uri, already_granted }
      selected: {},     // scope -> bool; "read" is always true and disabled
      submitting: false,
      submitError: '',
    }
  },
  computed: {
    sessionId () {
      return this.$route.query.session
    },
  },
  methods: {
    scopeLabel (scope) {
      return SCOPE_LABELS[scope] || scope
    },
    scopeDescription (scope) {
      return SCOPE_DESCRIPTIONS[scope] || ''
    },
    async loadConsent () {
      const url = `${process.env.VUE_APP_API_URL}/oauth/consent/${encodeURIComponent(this.sessionId)}`
      const response = await apiFetch(url, { method: 'GET' })

      if (response.status === 200) {
        const data = await response.json()
        this.consent = data

        // Pre-tick every requested scope; the user opts out of what they don't want.
        // "read" stays ticked and disabled.
        for (const scope of data.scopes) {
          this.selected[scope] = true
        }

        // If the user has a non-revoked grant that already covers this request,
        // skip the consent UI altogether — same UX as Google / GitHub / Claude.ai.
        if (data.already_granted) {
          await this.submit('allow', /* silent */ true)
          return
        }

        this.state = 'ready'
      } else if (response.status === 404) {
        this.state = 'error'
        this.errorMessage = 'This authorization request has expired or doesn\'t exist. Start the connection from your client again.'
      } else {
        // 401 is handled centrally by apiFetch (logout + redirect to login).
        // Anything else lands here.
        this.state = 'error'
        this.errorMessage = `Unexpected response from the server (HTTP ${response.status}).`
      }
    },
    async submit (decision, silent = false) {
      if (!silent) this.submitting = true
      this.submitError = ''

      const scopes = decision === 'allow'
        ? Object.keys(this.selected).filter(s => this.selected[s])
        : null

      const body = { session: this.sessionId, decision }
      if (scopes !== null) body.scopes = scopes

      const url = `${process.env.VUE_APP_API_URL}/oauth/authorize/consent`
      const response = await apiFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      if (response.status === 200) {
        const data = await response.json()
        // The redirect targets the client's own loopback / hosted URL — leave the SPA.
        window.location.replace(data.redirect)
      } else {
        const text = await response.text()
        this.submitting = false
        this.submitError = `Couldn't complete the request (HTTP ${response.status}). ${text}`
      }
    },
  },
  async created () {
    if (!this.sessionId) {
      this.state = 'error'
      this.errorMessage = 'Missing session identifier in the URL.'
      return
    }

    if (!this.$store.getters.isUserLoggedIn) {
      // Round-trip through login, then come back here with the session id intact.
      this.$router.replace({
        name: 'Login',
        query: { returnTo: `/oauth/consent?session=${encodeURIComponent(this.sessionId)}` },
      })
      return
    }

    await this.loadConsent()
  },
}
</script>
